import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { pickleballSkillCategories } from '../content/pickleball/skillCategories';
import { tennisSkillCategories } from '../content/tennis/skillCategories';
import { padelSkillCategories } from '../content/padel/skillCategories';
import { badmintonSkillCategories } from '../content/badminton/skillCategories';
import { golfSkillCategories } from '../content/golf/skillCategories';
import { hikingSkillCategories } from '../content/hiking/skillCategories';
import { squashSkillCategories } from '../content/squash/skillCategories';
import { racquetballSkillCategories } from '../content/racquetball/skillCategories';
import { skillProgressService } from '../services/skillProgressService';
import { SkillProgress, SkillCategory } from '../types/skillProgress';
import './SkillProgressTab.css';

const SkillProgressTab: React.FC = () => {
  const { currentSport, theme } = useTheme();
  const { user } = useAuth();
  const [skillProgress, setSkillProgress] = useState<SkillProgress[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [skillRating, setSkillRating] = useState<number>(3);
  const [skillNotes, setSkillNotes] = useState<string>('');

  // Get skill categories based on current sport
  const getSkillCategories = (): SkillCategory[] => {
    switch (currentSport.id) {
      case 'pickleball':
        return pickleballSkillCategories;
      case 'tennis':
        return tennisSkillCategories;
      case 'padel':
        return padelSkillCategories;
      case 'badminton':
        return badmintonSkillCategories;
      case 'golf':
        return golfSkillCategories;
      case 'hiking':
        return hikingSkillCategories;
      case 'squash':
        return squashSkillCategories;
      case 'racquetball':
        return racquetballSkillCategories;
      default:
        return [];
    }
  };

  const skillCategories = getSkillCategories();

  useEffect(() => {
    if (user && skillCategories.length > 0) {
      loadSkillProgress();
    }
  }, [user, currentSport.id, skillCategories.length]);

  const loadSkillProgress = async () => {
    if (!user) return;
    
    try {
      const progress = await skillProgressService.getSkillProgress(user.id, currentSport.id);
      setSkillProgress(progress);
    } catch (error) {
      console.error('Error loading skill progress:', error);
    }
  };

  const getSkillProgress = (skillId: string): SkillProgress | null => {
    return skillProgress.find(p => p.skillId === skillId) || null;
  };

  const getProgressStatus = (skillId: string): SkillProgress['status'] => {
    const progress = getSkillProgress(skillId);
    return progress?.status || 'not-started';
  };

  const getStatusIcon = (status: SkillProgress['status']) => {
    switch (status) {
      case 'not-started': return '⚪';
      case 'learning': return '🟡';
      case 'practicing': return '🟠';
      case 'mastered': return '✅';
      default: return '⚪';
    }
  };

  const getStatusColor = (status: SkillProgress['status']) => {
    switch (status) {
      case 'not-started': return '#ccc';
      case 'learning': return '#ffc107';
      case 'practicing': return '#ff9800';
      case 'mastered': return '#4caf50';
      default: return '#ccc';
    }
  };

  const getStatusText = (status: SkillProgress['status']) => {
    switch (status) {
      case 'not-started': return 'Not Started';
      case 'learning': return 'Learning';
      case 'practicing': return 'Practicing';
      case 'mastered': return 'Mastered';
      default: return 'Not Started';
    }
  };

  const handleSkillClick = (skillId: string) => {
    setSelectedSkill(skillId);
    const progress = getSkillProgress(skillId);
    setSkillRating(progress?.rating || 3);
    setSkillNotes(progress?.notes || '');
    setShowSkillModal(true);
  };

  const handleUpdateSkill = async () => {
    if (!user || !selectedSkill) return;

    try {
      const currentProgress = getSkillProgress(selectedSkill);
      let newStatus: SkillProgress['status'] = currentProgress?.status || 'not-started';

      // Auto-advance status based on rating
      if (skillRating >= 4) {
        newStatus = 'mastered';
      } else if (skillRating >= 3) {
        newStatus = 'practicing';
      } else if (skillRating >= 2) {
        newStatus = 'learning';
      }

      await skillProgressService.saveSkillProgress(user.id, currentSport.id, selectedSkill, {
        status: newStatus,
        rating: skillRating,
        notes: skillNotes,
        lastPracticed: new Date().toISOString(),
        completedAt: newStatus === 'mastered' ? new Date().toISOString() : undefined
      });

      await loadSkillProgress();
      setShowSkillModal(false);
    } catch (error) {
      console.error('Error updating skill progress:', error);
    }
  };

  const getCategoryProgress = (categoryId: string) => {
    const category = skillCategories.find(c => c.id === categoryId);
    if (!category) return { completed: 0, total: 0, percentage: 0 };

    const total = category.skills.length;
    const completed = category.skills.filter(skill => 
      getProgressStatus(skill.id) === 'mastered'
    ).length;

    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  const getOverallProgress = () => {
    const allSkills = skillCategories.flatMap(category => category.skills);
    const total = allSkills.length;
    const completed = allSkills.filter(skill => 
      getProgressStatus(skill.id) === 'mastered'
    ).length;

    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  const overallProgress = getOverallProgress();

  const getNoProgressMessage = () => {
    switch (currentSport.id) {
      case 'running':
        return {
          title: 'Running Progress',
          body: 'Track your form, cadence, and endurance over time. Use the Skill Assessment to test your knowledge of running fundamentals, then record your runs and watch your pace and consistency improve.'
        };
      case 'cycling':
        return {
          title: 'Cycling Progress',
          body: 'Monitor your pedaling efficiency, power output, and endurance. Complete the Skill Assessment to build your cycling knowledge, then track your rides and see your cadence and stamina improve.'
        };
      case 'swimming':
        return {
          title: 'Swimming Progress',
          body: 'Track your stroke technique, breathing rhythm, and lap times. Use the Skill Assessment to test your knowledge of swimming fundamentals, then log your swims and see your efficiency and endurance improve.'
        };
      case 'strength':
        return {
          title: 'Strength Progress',
          body: 'Track your lifts, form quality, and progressive overload. Complete the Skill Assessment for programming basics like bracing and progression, then log your workouts and watch your strength and technique improve.'
        };
      default:
        return {
          title: 'Skill Progress Tracking',
          body: 'Skill progress tracking is available for all sports! Switch to any sport to track your skill development.'
        };
    }
  };

  if (skillCategories.length === 0) {
    const msg = getNoProgressMessage();
    return (
      <div className="skill-progress-tab">
        <div className="no-progress-message">
          <h3>{msg.title}</h3>
          <p>{msg.body}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="skill-progress-tab">
      <div className="skill-progress-header">
        <h2>{currentSport.name} Skill Progress</h2>
        <p>Track your progress and master each skill</p>
      </div>

      {/* Overall Progress */}
      <div className="overall-progress-card">
        <div className="progress-header">
          <h3>Overall Progress</h3>
          <div className="progress-stats">
            <span className="progress-number">{overallProgress.completed}</span>
            <span className="progress-total">/ {overallProgress.total}</span>
            <span className="progress-percentage">({overallProgress.percentage}%)</span>
          </div>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ 
              width: `${overallProgress.percentage}%`,
              backgroundColor: theme.colors.primary 
            }}
          />
        </div>
      </div>

      {/* Skill Categories */}
      <div className="skill-categories">
        {skillCategories.map((category) => {
          const categoryProgress = getCategoryProgress(category.id);
          const isExpanded = expandedCategory === category.id;

          return (
            <div key={category.id} className="skill-category-card">
              <div 
                className="category-header"
                onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
              >
                <div className="category-info">
                  <h4 className="category-name">{category.name}</h4>
                  <p className="category-description">{category.description}</p>
                </div>
                <div className="category-progress">
                  <div className="category-stats">
                    <span className="category-completed">{categoryProgress.completed}</span>
                    <span className="category-total">/ {categoryProgress.total}</span>
                  </div>
                  <div className="category-percentage">{categoryProgress.percentage}%</div>
                  <div className="expand-icon">{isExpanded ? '▼' : '▶'}</div>
                </div>
              </div>

              {isExpanded && (
                <div className="skills-list">
                  {category.skills.map((skill) => {
                    const status = getProgressStatus(skill.id);
                    const progress = getSkillProgress(skill.id);

                    return (
                      <div 
                        key={skill.id} 
                        className={`skill-item ${status}`}
                        onClick={() => handleSkillClick(skill.id)}
                      >
                        <div className="skill-status">
                          <span 
                            className="status-icon"
                            style={{ color: getStatusColor(status) }}
                          >
                            {getStatusIcon(status)}
                          </span>
                        </div>
                        <div className="skill-info">
                          <h5 className="skill-name">{skill.name}</h5>
                          <p className="skill-description">{skill.description}</p>
                          <div className="skill-meta">
                            <span className="skill-difficulty">{skill.difficulty}</span>
                            {progress?.rating && (
                              <span className="skill-rating">
                                ⭐ {progress.rating}/5
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="skill-actions">
                          <span className="status-text">{getStatusText(status)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Skill Detail Modal */}
      {showSkillModal && selectedSkill && (
        <div className="modal-overlay" onClick={() => setShowSkillModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Update Skill Progress</h3>
              <button 
                className="close-button"
                onClick={() => setShowSkillModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {(() => {
                const skill = skillCategories
                  .flatMap(cat => cat.skills)
                  .find(s => s.id === selectedSkill);
                
                if (!skill) return null;

                return (
                  <>
                    <div className="skill-detail-header">
                      <h4 className="skill-detail-name">{skill.name}</h4>
                      <p className="skill-detail-description">{skill.description}</p>
                      <span className="skill-detail-difficulty">{skill.difficulty}</span>
                    </div>

                    <div className="skill-tips">
                      <h5>Tips:</h5>
                      <ul>
                        {skill.tips.map((tip, index) => (
                          <li key={index}>{tip}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="skill-rating-section">
                      <label className="rating-label">Rate your current level (1-5):</label>
                      <div className="rating-input">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            className={`rating-button ${skillRating === rating ? 'selected' : ''}`}
                            onClick={() => setSkillRating(rating)}
                          >
                            {rating}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="skill-notes-section">
                      <label className="notes-label">Notes (optional):</label>
                      <textarea
                        className="notes-textarea"
                        value={skillNotes}
                        onChange={(e) => setSkillNotes(e.target.value)}
                        placeholder="Add notes about your progress, challenges, or goals..."
                        rows={3}
                      />
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="modal-actions">
              <button
                className="update-skill-button"
                style={{ backgroundColor: theme.colors.primary }}
                onClick={handleUpdateSkill}
              >
                Update Progress
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillProgressTab;
